import React, { useState } from 'react';
import { useCompanyStore } from '../../../stores/companyStore';
import { notify } from '../../../stores/notificationStore';
import { confirm } from '../../../stores/confirmStore';
import {
  isVatExempt,
  formatCurrency,
  formatDate,
  formatVatRate,
} from '../../../utils/vatCalculations';
import { InvoiceTemplateProps, InvoiceItem } from '../types';
import { ActionBar } from '../shared/ActionBar';
import { UnifiedEmailModal, EmailData } from '../../modals/UnifiedEmailModal';
import { ItemsTable } from '../shared/ItemsTable';
import { PaymentSchedulesManagement } from '../../../features/payments';
import { publicLinksAPI, invoicesAPI } from '../../../utils/api';
import { logger } from '../../../utils/logger';

/**
 * Template Corporate - Agency (STARTER)
 * Design ultra-minimaliste noir et blanc selon le HTML/CSS fourni
 * Header noir, fond gris pour sections, mise en page épurée
 */
export const InvoiceTemplateCorporate: React.FC<InvoiceTemplateProps> = ({
  invoiceData,
  onClose,
  company: companyProp,
}) => {
  const { company: companyStore } = useCompanyStore();
  const [isLoading, setIsLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPaymentSchedules, setShowPaymentSchedules] = useState(false);
  const [publicLink, setPublicLink] = useState<string>('');

  const company = companyProp || companyStore;
  const isExempt = isVatExempt(company?.vatRegime);

  if (!invoiceData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 max-w-md text-center">
          <p className="text-red-600 font-semibold mb-4">Erreur : Aucune donnée de facture</p>
          <button onClick={onClose} className="px-4 py-2 bg-gradient-to-r from-teal-600 to-purple-600 hover:from-teal-700 hover:to-purple-700 text-white rounded shadow-lg shadow-teal-500/30 transition-all">
            Retour
          </button>
        </div>
      </div>
    );
  }

  const items: InvoiceItem[] = typeof invoiceData.items === 'string'
    ? JSON.parse(invoiceData.items).map((item: any) => {
        const unitPrice = Number(item.unitPrice) || 0;
        const quantity = Number(item.quantity) || 0;
        return {
          ...item,
          unitPrice,
          quantity,
          total: Number(item.totalHT) || Number(item.total) || (unitPrice * quantity),  // ✅ Fallback vers calcul
          description: item.description || '',
          vatRate: item.taxPercent || (isExempt ? 0 : 20),  // ✅ Prisma utilise taxPercent
          vatAmount: item.vatAmount || 0,
        };
      })
    : (invoiceData.items || []).map((item: any) => {
        const unitPrice = Number(item.unitPrice) || 0;
        const quantity = Number(item.quantity) || 0;
        return {
          ...item,
          unitPrice,
          quantity,
          total: Number(item.totalHT) || Number(item.total) || (unitPrice * quantity),  // ✅ Fallback vers calcul
          description: item.description || '',
          vatRate: item.taxPercent || (isExempt ? 0 : 20),  // ✅ Prisma utilise taxPercent
          vatAmount: item.vatAmount || 0,
        };
      });

  // ✅ Construire la ventilation TVA à partir des items (affichage uniquement)
  const vatBreakdown: Array<{ rate: number; baseHT: number; vatAmount: number }> = [];
  const vatByRate: Record<number, { baseHT: number; vatAmount: number }> = {};

  items.forEach((item: InvoiceItem) => {
    const rate = item.vatRate || 0;
    if (!vatByRate[rate]) {
      vatByRate[rate] = { baseHT: 0, vatAmount: 0 };
    }
    vatByRate[rate].baseHT += item.total || 0;
    vatByRate[rate].vatAmount += item.vatAmount || 0;
  });

  Object.entries(vatByRate).forEach(([rate, data]) => {
    vatBreakdown.push({
      rate: Number(rate),
      baseHT: data.baseHT,
      vatAmount: data.vatAmount
    });
  });

  // ✅ Utiliser les valeurs du backend (source de vérité)
  const subtotal = parseFloat(String(invoiceData.totalHT)) || parseFloat(String(invoiceData.subtotal)) || 0;
  const tax = parseFloat(String(invoiceData.totalTVA)) || parseFloat(String(invoiceData.tax)) || 0;
  const total = parseFloat(String(invoiceData.totalTTC)) || parseFloat(String(invoiceData.total)) || 0;

  const handleGeneratePDF = async () => {
    if (!invoiceData.id) {
      notify.error('ID de la facture manquant', 'Génération PDF');
      return;
    }
    setIsLoading(true);
    try {
      // ✅ Utilisation du backend pour générer le PDF (templates uniformisés)
      const blob = await invoicesAPI.downloadPDF(invoiceData.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const clientName = (invoiceData as any).Client?.fullName || (invoiceData as any).Client?.companyName || 'Client';
      a.download = `Facture_${invoiceData.invoiceNumber}_${clientName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      notify.success('PDF généré avec succès', 'Génération PDF');
    } catch (error) {
      logger.error('Erreur génération PDF:', error);
      notify.error("Erreur lors de la génération du PDF : " + (error as Error).message, 'Génération PDF');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateFacturX = async () => {
    if (!invoiceData.id) {
      notify.error('ID de la facture manquant', 'Factur-X');
      return;
    }
    setIsLoading(true);
    try {
      // ✅ Utilisation du backend pour générer le PDF Factur-X (templates uniformisés)
      const blob = await invoicesAPI.downloadFacturXPDF(invoiceData.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const clientName = (invoiceData as any).Client?.fullName || (invoiceData as any).Client?.companyName || 'Client';
      a.download = `Facture_FacturX_${invoiceData.invoiceNumber}_${clientName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      notify.success('PDF Factur-X généré avec succès', 'Factur-X');
    } catch (error) {
      logger.error('Erreur Factur-X:', error);
      notify.error("Erreur lors de la génération Factur-X : " + (error as Error).message, 'Factur-X');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenEmailModal = async () => {
    try {
      setIsLoading(true);
      // ✅ Utilisation de l'API Layer
      if (!invoiceData.id) {
        throw new Error('ID de la facture manquant');
      }
      const result = await publicLinksAPI.getInvoiceLink(invoiceData.id);
      if (result.success && result.data && result.data.length > 0) {
        const activeLink = result.data.find((link: any) => link.isActive);
        if (activeLink) {
          setPublicLink(activeLink.url);
        }
      }
    } catch (error) {
      logger.error('Erreur lors de la récupération du lien public:', error);
    } finally {
      setIsLoading(false);
      setShowEmailModal(true);
    }
  };

  const handleSendEmail = async (emailData: EmailData) => {
    try {
      if (!invoiceData?.id) {
        throw new Error('ID de la facture manquant');
      }

      // Générer le PDF Factur-X de la facture
      setIsLoading(true);
      const { generateFacturXPDF, convertInvoiceToFacturX } = await import('../../../utils/facturx');
      const facturxData = convertInvoiceToFacturX({ ...invoiceData, items: items }, company);
      const pdfBlob = await generateFacturXPDF(facturxData);

      // Préparer FormData pour l'envoi
      const formData = new FormData();
      formData.append('to', emailData.to);
      formData.append('subject', emailData.subject);
      formData.append('body', emailData.body);
      formData.append('pdf', pdfBlob, `Facture_${invoiceData.invoiceNumber}.pdf`);

      // ✅ Utilisation de l'API Layer
      await publicLinksAPI.sendInvoiceEmail(invoiceData.id, formData);

      notify.success(`Email envoyé à ${emailData.to}`, 'Envoi réussi');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur inconnue';
      notify.error(message, 'Erreur d\'envoi');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen print:min-h-0 print:bg-white">
      <ActionBar
        onGeneratePDF={handleGeneratePDF}
        onGenerateFacturX={handleGenerateFacturX}
        onSendEmail={handleOpenEmailModal}
        onPrint={handlePrint}
        onClose={onClose}
        onPaymentSchedules={() => setShowPaymentSchedules(true)}
        isLoading={isLoading}
        documentType="invoice"
        invoiceStatus={invoiceData.status}
      />

      {/* Payment Schedules Modal */}
      {showPaymentSchedules && invoiceData.id && (
        <PaymentSchedulesManagement
          invoiceId={invoiceData.id}
          invoiceNumber={invoiceData.invoiceNumber}
          invoiceTotal={total}
          onClose={() => setShowPaymentSchedules(false)}
        />
      )}

      <UnifiedEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSend={handleSendEmail}
        documentType="invoice"
        documentNumber={invoiceData.invoiceNumber}
        clientEmail={invoiceData.clientEmail || ''}
        clientName={invoiceData.clientName || ''}
        showPdfPreview={false}
        templateVariables={{
          invoice_number: invoiceData.invoiceNumber || '',
          client_name: invoiceData.clientName || '',
          total: total.toFixed(2),
          public_link: publicLink,
          due_date: formatDate(invoiceData.dueDate || invoiceData.issueDate),
          payment_conditions: company?.defaultPaymentTermsDays ? `Paiement à ${company.defaultPaymentTermsDays} jours` : 'Paiement à 30 jours',
          iban: company?.iban || '',
          bic: company?.bic || '',
          companyName: company?.legalName || company?.tradeName || '',
          companyEmail: company?.email || '',
          companyPhone: company?.phone || '',
        }}
      />

      <div className="py-8 print:py-0 print:mt-0">
        {/* Container - ombre légère */}
        <div className="max-w-4xl mx-auto bg-white shadow-lg print:shadow-none print:max-w-full overflow-hidden" id="invoice-template">

          {/* Main content - padding 3rem comme dans le CSS */}
          <div className="p-12 print:p-8">

            {/* Header */}
            <div className="flex justify-between items-start mb-8 pb-8">
              <div>
                <h1 className="text-4xl font-bold text-black mb-6">Facture</h1>
                <div className="text-sm leading-loose text-gray-800">
                  <p className="mb-2">
                    <span className="font-semibold">Numéro de facture :</span> {invoiceData.invoiceNumber}
                  </p>
                  <p className="mb-2">
                    <span className="font-semibold">Date d'émission :</span> {formatDate(invoiceData.issueDate)}
                  </p>
                  <p>
                    <span className="font-semibold">Date d'échéance :</span> {formatDate(invoiceData.dueDate)}
                  </p>
                </div>
              </div>

              {/* Logo à droite */}
              <div className="text-right">
                {company?.logoUrl && (
                  <img
                    src={company.logoUrl}
                    alt={company.legalName || company.tradeName || 'Logo'}
                    className="h-20 max-w-[200px] object-contain ml-auto"
                  />
                )}
              </div>
            </div>

            {/* Addresses */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-8 pb-8 border-b border-gray-300">
              {/* Émetteur */}
              <div className="text-sm leading-relaxed text-gray-800">
                <div className="font-semibold text-[15px] mb-2 text-black">
                  {company?.legalName || company?.tradeName}
                </div>
                {company?.addressLine1 && <p className="mb-1">{company.addressLine1}</p>}
                {(company?.zipCode || company?.city) && (
                  <p className="mb-1">
                    {company?.zipCode} {company?.city}, {company?.taxCountryCode || 'FR'}
                  </p>
                )}
                {company?.email && <p className="mb-1">{company.email}</p>}
              </div>

              {/* Client */}
              <div className="text-sm leading-relaxed text-gray-800">
                <div className="font-semibold text-[15px] mb-2 text-black">
                  {invoiceData.clientCompany || invoiceData.clientName}
                </div>
                {invoiceData.clientAddressLine1 && <p className="mb-1">{invoiceData.clientAddressLine1}</p>}
                {(invoiceData.clientZipCode || invoiceData.clientCity) && (
                  <p className="mb-1">
                    {invoiceData.clientZipCode} {invoiceData.clientCity}, FR
                  </p>
                )}
                {invoiceData.clientEmail && <p className="mb-1">{invoiceData.clientEmail}</p>}
              </div>
            </div>

            {/* Titre du document */}
            {invoiceData.title && (
              <div className="mb-8 pb-4 border-b border-gray-300 text-2xl font-normal tracking-wide text-gray-800">
                {invoiceData.title}
              </div>
            )}

            {/* TABLEAU DES ITEMS — délégué à ItemsTable (sections, remises, descriptions BTP) */}
            <ItemsTable
              items={items}
              tableStyle="bordered"
              accentColor="#111827"
              showVat={!isExempt}
            />

          </div>

          {/* Totals Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 px-12 py-8 bg-white border-t border-gray-400">
            <div></div>
            <div className="ml-auto w-full">
              <div className="flex justify-between py-2 text-[15px]">
                <span className="text-gray-700">Total HT</span>
                <span className="font-semibold text-black">{formatCurrency(subtotal)}</span>
              </div>

              {/* TVA - ventilation uniquement si plusieurs taux */}
              {!isExempt && (
                <>
                  {vatBreakdown.length > 1 ? (
                    /* Ventilation TVA si plusieurs taux */
                    vatBreakdown.map((vat, index) => (
                      <div key={index} className="flex justify-between py-2 text-[15px]">
                        <span className="text-gray-700">TVA {formatVatRate(vat.rate)}</span>
                        <span className="font-semibold text-black">{formatCurrency(vat.vatAmount)}</span>
                      </div>
                    ))
                  ) : tax > 0 && (
                    /* TVA simple si un seul taux */
                    <div className="flex justify-between py-2 text-[15px]">
                      <span className="text-gray-700">TVA {vatBreakdown.length === 1 ? formatVatRate(vatBreakdown[0].rate) : ''}</span>
                      <span className="font-semibold text-black">{formatCurrency(tax)}</span>
                    </div>
                  )}
                </>
              )}

              {/* Total TTC - fond gris foncé */}
              <div className="flex justify-between bg-gray-300 px-3 py-3 mt-2 font-bold text-base">
                <span className="text-black">Total TTC</span>
                <span className="text-black">{formatCurrency(total)}</span>
              </div>
            </div>
          </div>

          {/* Conditions - Fond gris */}
          <div className="px-12 py-8 bg-gray-200 text-xs text-gray-600 leading-relaxed border-t border-gray-400">
            <p>Pas d'escompte accordé pour paiement anticipé.</p>
            <p>En cas de non-paiement à la date d'échéance, des pénalités calculées à trois fois le taux d'intérêt légal seront appliquées.</p>
            <p>Tout retard de paiement entraînera une indemnité forfaitaire pour frais de recouvrement de 40€.</p>
            {company?.websiteUrl && (
              <p className="mt-3">
                <a href={company.websiteUrl} className="text-blue-600 hover:underline no-underline" target="_blank" rel="noopener noreferrer">
                  {company.websiteUrl.replace(/^https?:\/\//, '')}
                </a>
              </p>
            )}
          </div>

          {/* Conditions Générales de Vente */}
          {(company as any)?.legalNotesDefault && (
            <div className="px-12 py-8 bg-gray-50 border-t border-gray-200">
              <h3 className="text-sm font-bold text-gray-700 mb-3">Conditions Générales de Vente</h3>
              <div className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed">
                {(company as any).legalNotesDefault}
              </div>
            </div>
          )}

          {/* Notes Internes (affichées sur le document) */}
          {invoiceData.internalNotes && (
            <div className="px-12 py-8 bg-gray-100 border-t border-gray-300">
              <h3 className="text-sm font-bold text-gray-700 mb-3">Notes</h3>
              <p className="text-xs text-gray-600 whitespace-pre-wrap">{invoiceData.internalNotes}</p>
            </div>
          )}

          {/* Mentions particulières */}
          {invoiceData.termsConditions && (
            <div className="px-12 py-8 bg-gray-100 border-t border-gray-300">
              <h3 className="text-sm font-bold text-gray-700 mb-3">Mentions particulières</h3>
              <p className="text-xs text-gray-600 whitespace-pre-wrap">{invoiceData.termsConditions}</p>
            </div>
          )}

          {/* Notes - Legacy field */}
          {invoiceData.notes && !invoiceData.internalNotes && (
            <div className="px-12 py-8 bg-gray-50 border-t border-gray-200">
              <h3 className="text-sm font-bold text-gray-700 mb-3">Notes</h3>
              <p className="text-xs text-gray-600 whitespace-pre-wrap">{invoiceData.notes}</p>
            </div>
          )}

          {/* Informations légales uniquement */}
          {(company?.registrationNumber || company?.vatNumber || company?.legalForm || company?.capital || company?.rcsNumber || company?.apeCode) && (
            <div className="px-12 py-6 bg-white border-t border-gray-200">
              <p className="text-xs text-gray-600 text-center">
                {[
                  company?.registrationNumber && `SIRET: ${company.registrationNumber}`,
                  company?.vatNumber && `TVA: ${company.vatNumber}`,
                  company?.legalForm,
                  company?.capital && `Capital: ${company.capital}€`,
                  (company?.rcsNumber && company?.rcsCity) && `RCS ${company.rcsCity} ${company.rcsNumber}`,
                  company?.apeCode && `APE: ${company.apeCode}`
                ].filter(Boolean).join(' • ')}
              </p>
            </div>
          )}

          {/* Footer - fond gris */}
          <div className="flex justify-between items-center text-xs text-gray-800 px-12 py-4 pb-8 print:pb-16 bg-gray-200 border-t border-gray-400">
            <div>
              {(company?.legalName || company?.tradeName) && <p>{company?.legalName || company?.tradeName}, {company?.legalForm || 'SAS'}</p>}
            </div>
            <div className="text-right">
              <p>{invoiceData.invoiceNumber} · 1/1</p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};
